import { useHandler } from "@redotech/react-util/hook";
import { AutomationForgeElementType } from "@redotech/redo-content-builder-common/forge/marketing-automation-forge-types";
import { FlowInProgress } from "@redotech/redo-flows-common/flow-in-progress";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { useTranslation } from "@redotech/redo-i18n";
import { BaseReactFlow } from "@redotech/redo-web-automations/automations/flow";
import { toast } from "@redotech/redo-web/alert";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import "@xyflow/react/dist/style.css";
import { Dispatch, memo, SetStateAction, useMemo, useState } from "react";
import { DropEdge } from "./edges/drop-edge";
import { CustomEdge } from "./edges/edge";
import { LabeledEdge } from "./edges/labeled-edge";
import {
  buildNodesAndEdges,
  COLLAPSIBLE_NODE_TYPES,
  FlowEdge,
  NodeTypes,
} from "./flow-converter";
import "./flow.css";
import { AbTestNode } from "./nodes/ab-test-node";
import { BlockCheckoutNode } from "./nodes/block-checkout-node";
import { BranchNode } from "./nodes/branch-node";
import { CheckoutExperienceNode } from "./nodes/checkout-experience-node";
import { ConciergeBlockNode } from "./nodes/concierge-block-node";
import { CustomerTagActionNode } from "./nodes/customer-tag-action-node";
import { DownsellNode } from "./nodes/downsell-node";
import { DropZoneNode } from "./nodes/drop-zone-node";
import { EndNode } from "./nodes/end-node";
import { HsaConditionNode } from "./nodes/hsa-condition-node";
import { HsaContentNode } from "./nodes/hsa-content-node";
import { HsaEndNode } from "./nodes/hsa-end-node";
import { HsaKnockoutNode } from "./nodes/hsa-knockout-node";
import { HsaQuestionGroupNode } from "./nodes/hsa-question-group-node";
import { ManageStaticSegmentNode } from "./nodes/manage-static-segment-node";
import { OmsActionNode } from "./nodes/oms/oms-action-node";
import { OmsCodeNode } from "./nodes/oms/oms-code-node";
import { OmsConditionNode } from "./nodes/oms/oms-condition-node";
import { PostPurchasePageNode } from "./nodes/post-purchase-page-node";
import { PostPurchaseSegmentNode } from "./nodes/post-purchase-segment-node";
import { PostPurchaseSplitNode } from "./nodes/post-purchase-split-node";
import { ProcessReturnNode } from "./nodes/process-return-node";
import { RcoCheckoutPackageConditionNode } from "./nodes/rco-checkout-package-condition-node";
import { RcoCheckoutPackageNode } from "./nodes/rco-checkout-package-node";
import { RcoSegmentNode } from "./nodes/rco-segment-node";
import { RcoSplitNode } from "./nodes/rco-split-node";
import { RecognizeConditionNode } from "./nodes/recognize-condition-node";
import { RecognizeEmailNode } from "./nodes/recognize-email-node";
import { RecoverNode } from "./nodes/recover-node";
import { RuleActionsNode } from "./nodes/rules/rule-actions-node";
import { RuleConditionsNode } from "./nodes/rules/rule-conditions-node";
import { SendMessageNode } from "./nodes/send-message-node";
import { SendWebhookNode } from "./nodes/send-webhook-node";
import { ShopperAgentMessageNode } from "./nodes/shopper-agent-message-node";
import { SuppliesShippingConditionNode } from "./nodes/supplies-shipping-condition-node";
import { SuppliesShippingRateNode } from "./nodes/supplies-shipping-rate-node";
import { TrackingPageNode } from "./nodes/tracking-page-node";
import { TriggerNode } from "./nodes/trigger-node";
import { UpsellConditionNode } from "./nodes/upsell-condition-node";
import { UpsellNode } from "./nodes/upsell-node";
import { WaitForEventNode } from "./nodes/wait-for-event-node";
import { WaitNode } from "./nodes/wait-node";
import { WorkOrderConditionNode } from "./nodes/work-order/work-order-condition-node";
import { WorkOrderWorkStepNode } from "./nodes/work-order/work-order-work-step-node";
import { PartialFlowStep } from "./steps";
import { countDownstreamSteps } from "./utils/count-downstream-steps";
import { deleteElement } from "./utils/delete-element";
import { dropElement } from "./utils/drop-element";

interface CommonFlowChartProps {
  automation: FlowInProgress;
  selected: string | undefined;
  onNodeClick: (nodeId: string | undefined) => void;
  schema: Schema;
  partialFlow?: FlowInProgress;
  elementDrop?: ElementDrop | undefined;
  deleteElementOverride?:
    | ((id: string, steps: PartialFlowStep[]) => PartialFlowStep[])
    | undefined;
  showMiniMap?: boolean;
  hoveredNodeId?: string | null;
  /** Show collapse toggles for branching nodes with descendants. */
  collapsibleNodes?: boolean;
  /** Confirm deletes that would also remove downstream descendants. */
  confirmCascadingDelete?: boolean;
}

export type FlowChartProps = (
  | { readonly: true; setAutomation?: undefined; setModified?: undefined }
  | {
      readonly: false;
      setAutomation: Dispatch<SetStateAction<FlowInProgress | null>>;
      setModified: Dispatch<SetStateAction<boolean>>;
    }
) &
  CommonFlowChartProps;

export const Flowchart = memo(function Flowchart({
  automation,
  selected,
  onNodeClick,
  schema,
  partialFlow,
  elementDrop,
  deleteElementOverride,
  showMiniMap,
  hoveredNodeId,
  collapsibleNodes,
  confirmCascadingDelete,
  ...rest
}: FlowChartProps) {
  const { t } = useTranslation();
  const [collapsedNodeIds, setCollapsedNodeIds] = useState<Set<string>>(
    new Set(),
  );

  // Track the pending cascade delete confirmation.
  const [pendingCascadeDelete, setPendingCascadeDelete] = useState<{
    stepId: string;
    downstreamCount: number;
  } | null>(null);

  function clearDeletedStepSelection(remainingSteps: PartialFlowStep[]) {
    if (selected === undefined) {
      return;
    }

    const selectedStepStillExists = remainingSteps.some(
      (step) => step.id === selected,
    );

    if (selectedStepStillExists) {
      return;
    }

    onNodeClick(undefined);
  }

  const performDeleteElement = useHandler((id: string) => {
    if (rest.readonly) {
      return;
    }
    const newSteps = deleteElementOverride
      ? deleteElementOverride(id, automation.steps)
      : deleteElement(id, automation.steps);
    rest.setAutomation({ ...automation, steps: newSteps });
    rest.setModified(true);

    // A stale selection keeps the side panel open with no step to display.
    clearDeletedStepSelection(newSteps);
  });

  const toggleCollapse = useHandler((id: string) => {
    setCollapsedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  });

  const conditionNode =
    automation.category === "Recognize" ? RecognizeConditionNode : BranchNode;

  const nodeTypes: Record<NodeTypes, any> = {
    [NodeTypes.TRIGGER]: TriggerNode,
    [NodeTypes.WAIT]: WaitNode,
    [NodeTypes.WAIT_FOR_EVENT]: WaitForEventNode,
    [NodeTypes.BRANCH]: conditionNode,
    [NodeTypes.SEND_MESSAGE]: SendMessageNode,
    [NodeTypes.RECOGNIZE_EMAIL]: RecognizeEmailNode,
    [NodeTypes.SEND_WEBHOOK]: SendWebhookNode,
    [NodeTypes.CUSTOMER_TAG_ACTION]: CustomerTagActionNode,
    [NodeTypes.MANAGE_STATIC_SEGMENT]: ManageStaticSegmentNode,
    [NodeTypes.RECOVER]: RecoverNode,
    [NodeTypes.END]: EndNode,
    [NodeTypes.DROP_ZONE]: DropZoneNode,
    [NodeTypes.UPSELL]: UpsellNode,
    [NodeTypes.DOWNSELL]: DownsellNode,
    [NodeTypes.UPSELL_CONDITION]: UpsellConditionNode,
    [NodeTypes.OMS_ACTION]: OmsActionNode,
    [NodeTypes.OMS_CONDITION]: OmsConditionNode,
    [NodeTypes.OMS_CODE]: OmsCodeNode,
    [NodeTypes.RCO_SPLIT]: RcoSplitNode,
    [NodeTypes.CHECKOUT_EXPERIENCE]: CheckoutExperienceNode,
    [NodeTypes.BLOCK_CHECKOUT]: BlockCheckoutNode,
    [NodeTypes.RCO_SEGMENT]: RcoSegmentNode,
    [NodeTypes.RCO_CHECKOUT_PACKAGE]: RcoCheckoutPackageNode,
    [NodeTypes.RCO_CHECKOUT_PACKAGE_CONDITION]: RcoCheckoutPackageConditionNode,
    [NodeTypes.AB_TEST]: AbTestNode,
    [NodeTypes.POST_PURCHASE_PAGE]: PostPurchasePageNode,
    [NodeTypes.POST_PURCHASE_SEGMENT]: PostPurchaseSegmentNode,
    [NodeTypes.POST_PURCHASE_SPLIT]: PostPurchaseSplitNode,
    [NodeTypes.TRACKING_PAGE]: TrackingPageNode,
    [NodeTypes.RULE_CONDITIONS]: RuleConditionsNode,
    [NodeTypes.RULE_ACTIONS]: RuleActionsNode,
    [NodeTypes.WORK_ORDER_WORK_STEP]: WorkOrderWorkStepNode,
    [NodeTypes.WORK_ORDER_CONDITION]: WorkOrderConditionNode,
    [NodeTypes.PROCESS_RETURN]: ProcessReturnNode,
    [NodeTypes.CONCIERGE_BLOCK]: ConciergeBlockNode,
    [NodeTypes.SHOPPER_AGENT_MESSAGE]: ShopperAgentMessageNode,
    [NodeTypes.SUPPLIES_SHIPPING_CONDITION]: SuppliesShippingConditionNode,
    [NodeTypes.SUPPLIES_SHIPPING_RATE]: SuppliesShippingRateNode,
    [NodeTypes.HSA_QUESTION_GROUP]: HsaQuestionGroupNode,
    [NodeTypes.HSA_CONDITION]: HsaConditionNode,
    [NodeTypes.HSA_KNOCKOUT]: HsaKnockoutNode,
    [NodeTypes.HSA_CONTENT]: HsaContentNode,
    [NodeTypes.HSA_END]: HsaEndNode,
  };

  const edgeTypes = {
    edge: CustomEdge,
    labeled: LabeledEdge,
    dropEdge: DropEdge,
  };

  const elements = useMemo(() => {
    const raw = buildNodesAndEdges({
      automation: partialFlow ?? automation,
      elementDropped: (elementType, idFrom, idTo, conditionSide, edgeId) => {
        if (rest.readonly) {
          return;
        }

        const elementDropAllowed = elementDrop?.isElementDropAllowed({
          elementType,
          idFrom,
          idTo,
          flowInProgress: automation,
        }) ?? { isAllowed: true };
        if (elementDropAllowed.isAllowed) {
          const updatedSteps = dropElement({
            elementType,
            idFrom,
            idTo,
            conditionSide,
            edgeId,
            automation,
            schema,
            setSelected: onNodeClick,
          });

          rest.setAutomation({ ...automation, steps: updatedSteps });
          rest.setModified(true);
        } else {
          toast(elementDropAllowed.errorMessage, { variant: "error" });
        }
      },
      deleteElements: (id: string) => {
        if (rest.readonly) {
          return;
        }

        if (confirmCascadingDelete) {
          const downstreamCount = countDownstreamSteps(id, automation.steps);
          if (downstreamCount > 0) {
            setPendingCascadeDelete({ stepId: id, downstreamCount });
            return;
          }
        }

        performDeleteElement(id);
      },
      updateStep: (id: string, updatedStep: PartialFlowStep) => {
        if (rest.readonly) {
          return;
        }

        const newSteps = automation.steps.map((step) =>
          step.id === id ? updatedStep : step,
        );
        rest.setAutomation({ ...automation, steps: newSteps });
        rest.setModified(true);
      },
      selected,
      readonly: rest.readonly,
      hoveredNodeId,
      t,
    });

    if (!collapsibleNodes) {
      return raw;
    }

    const descendants = computeDescendantsByEdges(raw.edges);

    const hidden = new Set<string>();
    for (const collapsedId of collapsedNodeIds) {
      for (const descendant of descendants.get(collapsedId) ?? []) {
        hidden.add(descendant);
      }
    }

    const nodes = raw.nodes
      .filter((node) => !hidden.has(node.id))
      .map((node) => {
        if (!COLLAPSIBLE_NODE_TYPES.has(node.type)) {
          return node;
        }
        const descendantCount = descendants.get(node.id)?.size ?? 0;
        if (descendantCount === 0) {
          return node;
        }
        return {
          ...node,
          data: {
            ...node.data,
            isCollapsed: collapsedNodeIds.has(node.id),
            toggleCollapse: () => toggleCollapse(node.id),
            descendantCount,
          },
        };
      });

    const edges = raw.edges.filter(
      (edge) => !hidden.has(edge.source) && !hidden.has(edge.target),
    );

    return { nodes, edges };
  }, [
    partialFlow,
    automation,
    schema,
    selected,
    onNodeClick,
    elementDrop,
    rest,
    hoveredNodeId,
    collapsibleNodes,
    collapsedNodeIds,
    toggleCollapse,
    confirmCascadingDelete,
    performDeleteElement,
    t,
  ]);

  return (
    <Flex
      dir="column"
      flex={3.5}
      gap="none"
      height="full"
      position="relative"
      w="full"
    >
      <BaseReactFlow
        edgeTypes={edgeTypes}
        initialEdges={elements.edges}
        initialNodes={elements.nodes}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        showMiniMap={showMiniMap}
      />
      {pendingCascadeDelete && (
        <RedoModal
          isOpen
          modalSize="sm"
          onModalCloseRequested={() => setPendingCascadeDelete(null)}
          primaryButton={{
            text: t("Delete {{count}} nodes", {
              count: pendingCascadeDelete.downstreamCount + 1,
            }),
            onClick: () => {
              performDeleteElement(pendingCascadeDelete.stepId);
              setPendingCascadeDelete(null);
            },
            theme: "destructive",
            size: "sm",
          }}
          secondaryButton={{
            text: t("Cancel"),
            onClick: () => setPendingCascadeDelete(null),
            size: "sm",
          }}
          subtitle={t(
            "Deleting this node will also remove {{count}} downstream nodes. Continue?",
            { count: pendingCascadeDelete.downstreamCount },
          )}
          theme="error"
          title={t("Delete node and downstream chain?")}
        />
      )}
    </Flex>
  );
});

export interface ElementDrop {
  isElementDropAllowed: (input: {
    elementType: AutomationForgeElementType;
    idFrom: string;
    idTo?: string;
    flowInProgress: FlowInProgress;
  }) => { isAllowed: true } | { isAllowed: false; errorMessage: string };
}

/**
 * Builds a map from each node id to the set of all node ids reachable beneath
 * it, derived purely from the rendered edge graph. Generic to any flow type —
 * it has no knowledge of specific step types.
 */
function computeDescendantsByEdges(
  edges: FlowEdge[],
): Map<string, Set<string>> {
  const children = new Map<string, string[]>();
  for (const edge of edges) {
    if (!edge.source || !edge.target) {
      continue;
    }
    const existing = children.get(edge.source);
    if (existing) {
      existing.push(edge.target);
    } else {
      children.set(edge.source, [edge.target]);
    }
  }

  const descendantsById = new Map<string, Set<string>>();
  for (const start of children.keys()) {
    const visited = new Set<string>();
    const stack = [...(children.get(start) ?? [])];
    while (stack.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const id = stack.pop()!;
      if (visited.has(id)) {
        continue;
      }
      visited.add(id);
      for (const childId of children.get(id) ?? []) {
        if (!visited.has(childId)) {
          stack.push(childId);
        }
      }
    }
    descendantsById.set(start, visited);
  }

  return descendantsById;
}
